import { EMPTY_VALUE } from './dates';

/**
 * CPF, the Brazilian individual taxpayer number: eleven digits, rendered
 * `000.000.000-00`.
 *
 * A CPF is personal data. It is formatted for display and never used as a key
 * in a URL, a log line or anything the browser keeps -- see the lint rules in
 * `eslint.config.js`, which take the APIs that could do that away.
 */

const DIGITS = /\D/g;
export const CPF_LENGTH = 11;

/** Just the digits, which is the only form the API accepts. */
export function stripCpf(value: string): string {
  return value.replace(DIGITS, '');
}

/** `12345678909` becomes `123.456.789-09`. Anything unexpected is returned unchanged. */
export function formatCpf(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  const digits = stripCpf(value);
  if (digits.length !== CPF_LENGTH) {
    // Better to show what we were given than to mangle it into a shape that
    // looks authoritative and is not.
    return value;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * The same, but with everything that identifies a person removed:
 * `•••.•••.789-09`. For screens that need to confirm *which* record without
 * putting a whole CPF in front of whoever is looking over the shoulder.
 */
export function maskCpf(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  const digits = stripCpf(value);
  if (digits.length !== CPF_LENGTH) {
    return EMPTY_VALUE;
  }
  return `•••.•••.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/** Progressive formatting for a text input, so the mask appears as digits are typed. */
export function formatCpfWhileTyping(value: string): string {
  const digits = stripCpf(value).slice(0, CPF_LENGTH);
  const groups = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)].filter(
    (group) => group.length > 0,
  );
  const check = digits.slice(9);
  return check.length > 0 ? `${groups.join('.')}-${check}` : groups.join('.');
}

/**
 * Whether the check digits add up.
 *
 * Worth doing in the browser: a typo caught in the form is a question answered
 * immediately, while the same typo accepted and sent is a beneficiary attached
 * to the wrong person. The server validates too -- this is a courtesy, not the
 * authority.
 */
export function isValidCpf(value: string | null | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  const digits = stripCpf(value);
  if (digits.length !== CPF_LENGTH) {
    return false;
  }
  // Eleven of the same digit passes the arithmetic below but is never issued.
  if (/^(\d)\1{10}$/.test(digits)) {
    return false;
  }

  const checkDigit = (upTo: number): number => {
    let sum = 0;
    for (let index = 0; index < upTo; index += 1) {
      sum += Number(digits[index]) * (upTo + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}
